import React, { useMemo, useCallback } from 'react';

import { snackbar } from '@occc-app/design-system';
import { TableRowActionsMenu, useConfirmDialog } from '@occc/ui-design-system';
import { GetAllowListPhoneNumbersQuery } from '@occc/graphql';

import { DeleteAllowListPhoneNumberConfirmDialog } from './delete-allow-list-phone-number.confirm-dialog';
import { deleteError, deleteSuccess } from './allow-list.copies';
import { useDeleteAllowListPhoneNumberMutation } from './hooks/allow-list.query.hook';

type AllowListActionsContainerProps = {
  uuid: string;
  name: string;
  isUserPermittedToEdit: boolean;
  isUserPermittedToDelete: boolean;
  onEditClick: () => void;
};

export const AllowListActionsContainer = ({
  uuid,
  name,
  isUserPermittedToEdit,
  isUserPermittedToDelete,
  onEditClick,
}: AllowListActionsContainerProps) => {
  const onDeleteCompleted = useCallback(() => snackbar.success(deleteSuccess), []);
  const onDeleteError = useCallback(() => snackbar.error(deleteError), []);

  const [deleteAllowListPhoneNumberMutation, { loading: deleteLoading }] =
    useDeleteAllowListPhoneNumberMutation({
      variables: { input: { allowedPhoneNumberUuid: uuid } },
      onCompleted: onDeleteCompleted,
      onError: onDeleteError,
      refetchQueries: ['getAllowListPhoneNumbers' satisfies keyof GetAllowListPhoneNumbersQuery],
    });

  const { open, setOpen, onCloseButtonClick, onConfirmButtonClick } = useConfirmDialog({
    onConfirm: deleteAllowListPhoneNumberMutation,
    shouldStopEventPropagation: true,
  });

  const menuItems = useMemo(() => {
    const items = [];

    if (isUserPermittedToEdit) {
      items.push({
        label: 'Edit',
        onClick: onEditClick,
        dataTestId: 'edit',
      });
    }

    if (isUserPermittedToDelete) {
      items.push({
        label: 'Delete',
        onClick: () => setOpen(true),
        dataTestId: 'delete',
      });
    }

    return items;
  }, [isUserPermittedToEdit, isUserPermittedToDelete, onEditClick, setOpen]);

  return (
    <>
      <TableRowActionsMenu menuItems={menuItems} iconButtonSx={{ padding: null }} />

      {open && (
        <DeleteAllowListPhoneNumberConfirmDialog
          onClose={onCloseButtonClick}
          onConfirm={onConfirmButtonClick}
          loading={deleteLoading}
          entityLabel={name}
        />
      )}
    </>
  );
};
